import { useMutation, type UseMutationOptions } from '@tanstack/react-query'
import * as z from 'zod'

import { customInstance } from '@/api/client'
import {
  type AffiliateAdminCouponSchema,
  affiliateAdminCouponSchema,
  type CouponBatchRequest,
} from '@/domain/coupons'

// TODO(MKT-745): temporary shim — replace with the orval-generated hook once
// POST /v4/affiliates/admin/partners/{partnerId}/coupons/batch/ is on dev. The
// endpoint may not exist on dev yet; a separate backend PR is adding it. The
// request contract below is the one that PR must match. Mirrors the custom-hook
// pattern in pages/admin/settlements/api.ts.

export type CreateCouponBatchVariables = {
  partnerId: string
  data: CouponBatchRequest
}

export const createCouponBatch = async (
  partnerId: string,
  data: CouponBatchRequest,
): Promise<AffiliateAdminCouponSchema[]> => {
  const response = await customInstance<unknown>({
    url: `/v4/affiliates/admin/partners/${partnerId}/coupons/batch/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data,
  })

  // The batch endpoint isn't on dev yet (see TODO above); validate the shape
  // loudly rather than silently feeding a wrapped/paginated/malformed body into
  // the results dialog and CSV, which both assume a bare array of coupons. A
  // mismatch throws (subsuming the old bare `Array.isArray` check) and surfaces
  // via the caller's MutationError.
  return z.array(affiliateAdminCouponSchema).parse(response)
}

/**
 * Generate a batch of affiliate links. Resolves to the array of generated
 * coupons (the existing `AffiliateAdminCouponSchema`). Accepts the same
 * `{ mutation }` options shape as the orval-generated coupon hooks so the call
 * site reads identically.
 */
export const useCreateCouponBatch = (options?: {
  mutation?: UseMutationOptions<AffiliateAdminCouponSchema[], unknown, CreateCouponBatchVariables>
}) =>
  useMutation({
    mutationFn: ({ partnerId, data }: CreateCouponBatchVariables) =>
      createCouponBatch(partnerId, data),
    ...options?.mutation,
  })
